import { IconEdit, IconLoader, IconX } from '@tabler/icons-react'
import React, { useEffect, useState } from 'react'
import { PostSubAttributes } from 'src/API/rest.subattribute'
import { useStatesAPI } from 'src/API/rest.states'
import { ButtonIconSquare } from 'src/components/buttons'
import { InputStandard } from 'src/components/standard.input'
import { ModalStandard, ModalStandardContent, ModalStandardDescription, ModalStandardFooter, ModalStandardHeader, ModalStandardTitle } from 'src/components/standard.modal'
import { toast } from 'src/shadcn/ui/use-toast'

type Props = {
    show?: boolean,
    setShow?: (show: boolean) => void,
}

export function ASPUpdateModal(props: Props) {

    const {
        states,
        isFetching: isFetchingStates,
    } = useStatesAPI({
        trigger: props.show,
        isState: 1
    })

    const { updateSubAttributeBulkASP, isUpdatingSubAttributeBulkASP } = PostSubAttributes()

    const [aspValues, setAspValues] = useState<{ [key: number]: string }>({})

    useEffect(() => {
        if (props.show && states) {
            const initialValues: { [key: number]: string } = {};
            states.forEach(state => {
                const asp = String(state.FM04_ASP_percentage || ''); // Assuming API returns '20.00' format string, or handle number
                initialValues[state.FM04_StateID] = asp;
            });
            setAspValues(initialValues)
        }
    }, [props.show, states])

    const handleInputChange = (stateId: number, value: string) => {
        setAspValues(prev => ({
            ...prev,
            [stateId]: value
        }))
    }

    const onSubmit = async () => {
        const payload = Object.entries(aspValues).map(([stateId, aspValue]) => ({
            state_id: Number(stateId),
            asp_percentage: Number(aspValue)
        })).filter(item => !isNaN(item.asp_percentage) && aspValues[item.state_id] !== '')

        if (payload.length === 0) {
            toast({
                title: "Error",
                description: "Please enter at least one ASP percentage",
            })
            return
        }

        try {
            const response = await updateSubAttributeBulkASP(payload)
            if (response.success) {
                toast({
                    title: "Success",
                    description: response.message || "Updated successfully",
                })
                props.setShow?.(false)
            } else {
                toast({
                    title: "Error",
                    description: response.message,
                })
            }
        } catch (e: any) {
            toast({
                title: "Error",
                description: e.message || "An error occurred",
            })
        }
    }

    return (
        <ModalStandard
            open={props.show}
            onOpenChange={props.setShow}
        >
            <ModalStandardContent className='bg-vstargray-100 max-w-4xl max-h-[90vh] flex flex-col overflow-hidden'>
                <ModalStandardHeader >
                    <ModalStandardTitle >ASP Bulk Update</ModalStandardTitle>
                    <ModalStandardDescription>
                        Enter ASP Percentage For Relevant States
                    </ModalStandardDescription>
                </ModalStandardHeader>

                <div className='flex-grow overflow-auto py-4 min-h-0'>
                    {isFetchingStates ? (
                        <div className="flex justify-center p-4"><IconLoader className="animate-spin" /></div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 px-2">
                            {states?.map(state => (
                                <div key={state.FM04_StateID} className="p-3 border rounded bg-white shadow-sm">
                                    <label className="text-sm font-medium text-gray-700 block mb-1">{state.FM04_StateName}</label>
                                    <InputStandard
                                        type="number"
                                        placeholder="ASP %"
                                        value={aspValues[state.FM04_StateID]}
                                        onChange={(e) => handleInputChange(state.FM04_StateID, e.target.value)}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <ModalStandardFooter>
                    <ButtonIconSquare
                        variant='white'
                        icon={<IconX />}
                        text='Close'
                        onClick={() => props.setShow?.(false)}
                        disabled={isUpdatingSubAttributeBulkASP}
                    />
                    <ButtonIconSquare
                        variant='secondary'
                        icon={isUpdatingSubAttributeBulkASP ? <IconLoader className=' animate-spin ' /> : <IconEdit />}
                        text='Update'
                        onClick={onSubmit}
                        disabled={isUpdatingSubAttributeBulkASP}
                    />
                </ModalStandardFooter>
            </ModalStandardContent>
        </ModalStandard>
    )
}
